import { UUID } from 'crypto'
import {
  User,
  OnboardingStatusResponse,
  OnboardingDataResponse,
} from '../models/Users'
import { useAuth0 } from '@/utils/auth/auth0'

const serverURL = import.meta.env.VITE_API_URL

export class UserConflictError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'UserConflictError'
  }
}

export async function checkRegistration(token: string) {
  const response = await fetch(`${serverURL}/users/`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    if (response.status === 409) {
      throw new UserConflictError('User already registered with this email')
    }

    throw new Error(`Error fetching user`)
  }

  const user = await response.json()

  if (!user) {
    throw new Error(`Error fetching user`)
  }

  return user
}

interface UpdateUserFn {
  token: string
  editedUser: User
}

export async function updateUser({
  token,
  editedUser,
}: UpdateUserFn): Promise<User> {
  const response = await fetch(`${serverURL}/users/${editedUser.uuid}`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(editedUser),
  })

  if (!response.ok) {
    throw new Error('Error updating user')
  }

  const updatedUser = await response.json()
  return updatedUser
}

interface UpdateUserTermsFn {
  token: string
  userUuid: UUID
}
export async function updateUserTermsAccepted({
  token,
  userUuid,
}: UpdateUserTermsFn): Promise<User> {
  const response = await fetch(`${serverURL}/users/accept-terms/${userUuid}`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  })

  const responseBody = await response.json()

  return responseBody
}

interface GetUserFn {
  user_uuid: UUID
  token: string
}

export async function getUserByUuid({
  user_uuid,
  token,
}: GetUserFn): Promise<User> {
  const response = await fetch(`${serverURL}/users/${user_uuid}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  const user = await response.json()
  return user
}

export async function fetchUserOrgs(token: string, user_uuid: UUID) {
  const response = await fetch(`${serverURL}/users/${user_uuid}/orgs`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  const orgsData = await response.json()

  return orgsData
}

interface GetOnboardingStatusFn {
  user_uuid: UUID
  token: string
  includeDebug?: boolean
}

/**
 * Fetches the complete onboarding wizard status from the unified API endpoint.
 * This function eliminates race conditions by getting all required data in a single request.
 */
export async function getUserOnboardingStatus({
  user_uuid,
  token,
  includeDebug = false,
}: GetOnboardingStatusFn): Promise<OnboardingStatusResponse> {
  const url = new URL(`${serverURL}/users/${user_uuid}/onboarding-status`)

  // Add query parameter for debug info if requested
  if (includeDebug) {
    url.searchParams.append('include_debug', 'true')
  }

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch onboarding status: ${response.status}`)
  }

  const onboardingStatus = await response.json()
  return onboardingStatus
}

interface GetOnboardingDataFn {
  user_uuid: UUID
  token: string
  includeDebug?: boolean
  selected_org_uuid?: UUID
}

/**
 * Fetches the pure data onboarding status from the new API endpoint.
 * This endpoint returns only raw data states and counts without step logic,
 * allowing the frontend to own all step management and UI logic.
 */
export async function getUserOnboardingData({
  user_uuid,
  token,
  includeDebug = false,
  selected_org_uuid,
}: GetOnboardingDataFn): Promise<OnboardingDataResponse> {
  const url = new URL(`${serverURL}/users/${user_uuid}/onboarding-data`)

  // Add query parameter for debug info if requested
  if (includeDebug) {
    url.searchParams.append('include_debug', 'true')
  }

  // Add query parameter for selected org UUID if provided
  if (selected_org_uuid) {
    url.searchParams.append('selected_org_uuid', selected_org_uuid)
  }

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch onboarding data: ${response.status}`)
  }

  const onboardingData = await response.json()
  return onboardingData
}

export async function getOrgUserPermissions(
  token: string,
  orgUuid: UUID,
  userUuid: UUID,
) {
  if (!token || !orgUuid || !userUuid) {
    throw new Error('Invalid input parameters')
  }
  const response = await fetch(
    `${serverURL}/orgs/${orgUuid}/${userUuid}/permissions`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  )

  if (!response.ok) {
    throw new Error(`Error fetching permissions`)
  }

  const permissions = await response.json()

  return permissions
}

export async function resetPassword(token: string) {
  const response = await fetch(`${serverURL}/users/reset-password`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    throw new Error(`Error resetting password`)
  }

  const data = await response.json()
  return data
}

export async function fetchAuth0Identities(token: string, auth0_id: string) {
  const response = await fetch(`${serverURL}/users/identities/${auth0_id}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    throw new Error(`Error fetching identities`)
  }

  const data = await response.json()
  return data
}

export async function linkAuth0Identities(
  token: string,
  primaryAuth0Id: string,
  secondaryAuth0Id: string,
) {
  const response = await fetch(`${serverURL}/users/link-accounts`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      primary_auth0_id: primaryAuth0Id,
      secondary_auth0_id: secondaryAuth0Id,
    }),
  })

  return response
}

export async function unLinkAuth0Identities(
  token: string,
  primaryAuth0Id: string,
  secondaryMethodProvider: string,
  secondaryAuth0Id: string,
) {
  const response = await fetch(`${serverURL}/users/unlink-account`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      primary_auth0_id: primaryAuth0Id,
      secondary_method_provider: secondaryMethodProvider,
      secondary_auth0_id: secondaryAuth0Id,
    }),
  })

  if (!response.ok) {
    throw new Error(`Error unlinking secondary account`)
  }

  const data = await response.json()
  return data
}
